// Prompt Evolution Studio Types

export interface PromptEvolutionSession {
  id: string;
  title: string;
  description?: string;
  initialPrompt: string;
  currentPrompt: string;
  targetUseCase?: string; // e.g., 'image-generation', 'story-writing', 'technical'
  iterations: PromptEvolutionIteration[];
  metadata: SessionMetadata;
  createdAt: string;
  updatedAt: string;
}

export interface PromptEvolutionIteration {
  id: string;
  iterationNumber: number;
  agentType: string; // The niche agent used
  agentName: string;
  inputPrompt: string;
  generatedPrompts: GeneratedPrompt[];
  selectedPromptIndex?: number; // Which prompt was chosen to continue
  customInstructions?: string;
  timestamp: string;
  // Raw response data from agent for debugging/display
  agentResponse?: {
    enhancedPrompt?: string;
    previousPrompt?: string;
    agentType?: string;
    focusArea?: string;
    success?: boolean;
    error?: string;
    iterationNumber?: number;
    sessionId?: string;
    // Any other fields from the response
    [key: string]: any;
  };
}

export interface GeneratedPrompt {
  id: string;
  prompt: string;
  score?: number; // Optional quality score
  explanation?: string; // Why this prompt was generated
  tags?: string[]; // Descriptive tags
  metadata?: Record<string, any>;
}

export interface SessionMetadata {
  totalIterations: number;
  agentsUsed: string[];
  generatedImages?: GeneratedImage[];
  exportHistory: ExportRecord[];
  tags: string[];
  temperature?: number;
  maxTokens?: number;
  backendSessionId?: string; // Backend session ID for /api/agent/prompt API
}

export interface GeneratedImage {
  id: string;
  promptUsed: string;
  iterationId: string;
  imageUrl?: string;
  provider: string;
  generatedAt: string;
  metadata?: Record<string, any>;
}

export interface ExportRecord {
  exportedAt: string;
  format: 'json' | 'markdown' | 'text';
  includesImages: boolean;
}

// Agent types available for prompt evolution
export interface PromptEvolutionAgent {
  id: string;
  name: string;
  category: AgentCategory;
  description: string;
  specialties: string[];
  icon?: string;
  endpoint?: string; // Optional custom endpoint
}

export type AgentCategory = 
  | 'creative'
  | 'technical'
  | 'visual'
  | 'narrative'
  | 'scientific'
  | 'marketing'
  | 'educational'
  | 'specialized';

// Request/Response types for agent interactions
export interface PromptEvolutionRequest {
  sessionId?: string;
  prompt: string;
  agentType: string;
  customInstructions?: string;
  numberOfVariants?: number; // Default 3
  temperature?: number;
  maxTokens?: number;
}

export interface PromptEvolutionResponse {
  success: boolean;
  iterationId: string;
  generatedPrompts: GeneratedPrompt[];
  agentInsights?: string; // Agent's explanation of improvements
  error?: string;
}

// Session management
export interface PromptEvolutionFilter {
  agentTypes?: string[];
  categories?: AgentCategory[];
  dateRange?: {
    from: string;
    to: string;
  };
  hasImages?: boolean;
}

export interface PromptEvolutionStats {
  totalSessions: number;
  totalIterations: number;
  averageIterationsPerSession: number;
  mostUsedAgents: Array<{ agent: string; count: number }>;
  totalImagesGenerated: number;
}

// Available niche agents for prompt evolution
export const PROMPT_EVOLUTION_AGENTS: PromptEvolutionAgent[] = [
  // Creative Agents
  {
    id: 'abstract-thinker',
    name: 'Abstract Thinker',
    category: 'creative',
    description: 'Explores conceptual and philosophical dimensions of prompts',
    specialties: ['conceptual expansion', 'philosophical depth', 'abstract reasoning'],
  },
  {
    id: 'image-prompt-iterator',
    name: 'Image Prompt Specialist',
    category: 'visual',
    description: 'Optimizes prompts for image generation with style and composition focus',
    specialties: ['visual description', 'artistic style', 'composition', 'lighting'],
  },
  {
    id: 'story-generator',
    name: 'Narrative Architect',
    category: 'narrative',
    description: 'Enhances prompts with narrative structure and storytelling elements',
    specialties: ['plot development', 'character depth', 'world-building', 'dialogue'],
  },
  {
    id: 'character-agent',
    name: 'Character Developer',
    category: 'narrative',
    description: 'Adds character depth and personality to prompts',
    specialties: ['character traits', 'backstory', 'motivations', 'relationships'],
  },
  {
    id: 'scene-agent',
    name: 'Scene Designer',
    category: 'visual',
    description: 'Enriches prompts with atmospheric and environmental details',
    specialties: ['atmosphere', 'setting', 'mood', 'sensory details'],
  },
  {
    id: 'urban-culture',
    name: 'Urban Culture Expert',
    category: 'specialized',
    description: 'Infuses prompts with urban culture and street art aesthetics',
    specialties: ['street art', 'urban landscapes', 'cultural fusion', 'contemporary style'],
  },
  {
    id: 'fashion-expert',
    name: 'Fashion Consultant',
    category: 'specialized',
    description: 'Adds fashion and style elements to prompts',
    specialties: ['fashion trends', 'clothing design', 'style aesthetics', 'accessories'],
  },
  {
    id: 'scientific-agent',
    name: 'Scientific Advisor',
    category: 'scientific',
    description: 'Adds scientific accuracy and technical details to prompts',
    specialties: ['technical accuracy', 'scientific concepts', 'research-based details'],
  },
  {
    id: 'marketing-agent',
    name: 'Marketing Strategist',
    category: 'marketing',
    description: 'Optimizes prompts for marketing and commercial appeal',
    specialties: ['brand messaging', 'target audience', 'emotional appeal', 'call-to-action'],
  },
  {
    id: 'ux-designer',
    name: 'UX/UI Specialist',
    category: 'technical',
    description: 'Enhances prompts for user interface and experience design',
    specialties: ['user flow', 'interface design', 'accessibility', 'usability'],
  },
  {
    id: 'coding-assistant',
    name: 'Technical Architect',
    category: 'technical',
    description: 'Adds technical implementation details to prompts',
    specialties: ['code structure', 'algorithms', 'best practices', 'documentation'],
  },
  {
    id: 'journalism-agent',
    name: 'Editorial Expert',
    category: 'narrative',
    description: 'Refines prompts with journalistic clarity and structure',
    specialties: ['clarity', 'fact-based', 'structured narrative', 'headline optimization'],
  },
  {
    id: 'comics-specialist',
    name: 'Comics & Manga Artist',
    category: 'visual',
    description: 'Adds comic book and manga styling to prompts',
    specialties: ['panel composition', 'visual storytelling', 'manga aesthetics', 'action sequences'],
  },
  {
    id: 'animation-storyboard',
    name: 'Animation Director',
    category: 'visual',
    description: 'Optimizes prompts for animation and motion',
    specialties: ['motion', 'timing', 'keyframes', 'cinematography'],
  },
  {
    id: 'music-visualizer',
    name: 'Music Visualization Expert',
    category: 'specialized',
    description: 'Creates prompts that visualize music and sound',
    specialties: ['synesthesia', 'rhythm visualization', 'audio-visual correlation'],
  },
  {
    id: 'paleontology-expert',
    name: 'Prehistoric Specialist',
    category: 'scientific',
    description: 'Adds prehistoric and paleontological accuracy to prompts',
    specialties: ['prehistoric life', 'geological periods', 'fossil details', 'ancient environments'],
  },
  {
    id: 'architectural-designer',
    name: 'Architecture Visionary',
    category: 'technical',
    description: 'Enhances prompts with architectural and spatial design elements',
    specialties: ['building design', 'spatial composition', 'materials', 'structural elements'],
  },
  {
    id: 'psychology-expert',
    name: 'Psychology Consultant',
    category: 'scientific',
    description: 'Adds psychological depth and behavioral insights to prompts',
    specialties: ['emotions', 'behavior patterns', 'mental states', 'cognitive processes'],
  },
  {
    id: 'mythology-expert',
    name: 'Mythology & Folklore Sage',
    category: 'specialized',
    description: 'Infuses prompts with mythological and folkloric elements',
    specialties: ['mythical creatures', 'legendary narratives', 'cultural symbolism'],
  },
  {
    id: 'cuisine-specialist',
    name: 'Culinary Artist',
    category: 'specialized',
    description: 'Adds culinary and gastronomic details to prompts',
    specialties: ['food styling', 'culinary techniques', 'flavor profiles', 'presentation'],
  },
];

// LORA Training Types
export interface LoraGenerationRequest {
  basePrompt: string;
  numberOfPrompts: number;
  promptStyle: 'short' | 'detailed';
  variations?: string[]; // Specific variation themes
  seed?: number;
}

export interface LoraGenerationResponse {
  success: boolean;
  prompts: LoraTrainingPrompt[];
  metadata?: {
    basePrompt: string;
    style: 'short' | 'detailed';
    generatedAt: string;
  };
  error?: string;
}

export interface LoraTrainingPrompt {
  id: string;
  prompt: string;
  category?: string; // e.g., 'angle', 'lighting', 'expression', 'background'
  weight?: number; // Training weight suggestion
}

// Helper functions
export const getAgentsByCategory = (category: AgentCategory): PromptEvolutionAgent[] => {
  return PROMPT_EVOLUTION_AGENTS.filter(agent => agent.category === category);
};

export const getAgentById = (id: string): PromptEvolutionAgent | undefined => {
  return PROMPT_EVOLUTION_AGENTS.find(agent => agent.id === id);
};